import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Badge,
  Avatar,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import api from '../../services/api';
import RoleSelector from '../RoleSelector';

// Mapeamento de títulos por tipo de usuário
const DASHBOARD_TITLES = {
  supervisor: '🎓 Painel do Supervisor',
  arguente: '🎯 Painel do Arguente',
  presidente: '👨‍💼 Painel do Presidente',
  estudante: '📚 Painel do Estudante',
  chefe_departamento: '🏢 Painel do Chefe de Departamento',
  direcao_cientifica: '🔬 Painel da Direção Científica',
  revisor: '📋 Painel do Revisor',
  revisor_projeto: '📋 Painel do Revisor',
  admin: '⚙️ Painel Administrativo',
  instituicao: '🏛️ Painel da Instituição'
};

// Mapeamento de cores por tipo de usuário
const USER_TYPE_COLORS = {
  supervisor: '#1976d2',
  arguente: '#9c27b0',
  presidente: '#d32f2f',
  estudante: '#2e7d32',
  chefe_departamento: '#ed6c02',
  direcao_cientifica: '#0288d1',
  revisor: '#7b1fa2',
  revisor_projeto: '#7b1fa2',
  admin: '#424242',
  instituicao: '#5d4037'
};

// Configurações padrão de sessão
const DEFAULT_SESSION_CONFIG = {
  timeoutMinutes: 30,        // Timeout total da sessão
  warningMinutes: 2,         // Aviso antes do logout
  checkIntervalMinutes: 5    // Verificação de token
};

const DashboardHeader = ({ 
  onRefresh, 
  refreshing = false, 
  sessionTimeout = 30,
  showBackButton = true,
  customTitle = null 
}) => {
  const { user, logout, isAuthenticated } = useAuth();
  const { naoLidas } = useNotificacao();
  const navigate = useNavigate();
  const location = useLocation();

  // Estados da gestão de sessão
  const [sessionWarning, setSessionWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [sessionActive, setSessionActive] = useState(true);

  // Refs para gerenciar timers
  const sessionTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const tokenCheckTimerRef = useRef(null);
  const countdownTimerRef = useRef(null);

  // ============================================
  // HANDLERS DE SESSÃO
  // ============================================
  const handleSessionExpired = useCallback(() => {
    // Limpar todos os timers
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (tokenCheckTimerRef.current) clearInterval(tokenCheckTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    setSessionActive(false);
    setSessionWarning(false);
    
    // Logout e redirecionamento
    logout();
    navigate('/login', { 
      state: { 
        message: 'Sua sessão expirou. Por favor, faça login novamente.',
        from: location.pathname 
      } 
    });
  }, [logout, navigate, location]);

  // ============================================
  // VERIFICAÇÃO DE TOKEN
  // ============================================
  const checkTokenValidity = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      // Tenta fazer uma chamada simples para verificar se o token ainda é válido
      await api.get('/auth/verify-token');
    } catch (error) {
      console.warn('Token inválido detectado:', error);
      handleSessionExpired();
    }
  }, [isAuthenticated, handleSessionExpired]);

  // ============================================
  // GESTÃO DE ATIVIDADE DO USUÁRIO
  // ============================================
  const resetSessionTimer = useCallback(() => {
    if (!isAuthenticated || !sessionActive) return;

    // Limpar timers existentes
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);

    // Fechar warning se estiver aberto
    setSessionWarning(false);

    const timeoutMs = sessionTimeout * 60 * 1000; // Converter para milliseconds
    const warningMs = DEFAULT_SESSION_CONFIG.warningMinutes * 60 * 1000;

    // Timer para mostrar warning
    warningTimerRef.current = setTimeout(() => {
      if (sessionActive) {
        setSessionWarning(true);
        setTimeRemaining(DEFAULT_SESSION_CONFIG.warningMinutes * 60);
        
        // Countdown do warning
        countdownTimerRef.current = setInterval(() => {
          setTimeRemaining(prev => {
            if (prev <= 1) {
              handleSessionExpired();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }
    }, timeoutMs - warningMs);

    // Timer para logout automático
    sessionTimerRef.current = setTimeout(() => {
      if (sessionActive) {
        handleSessionExpired();
      }
    }, timeoutMs);
  }, [sessionTimeout, isAuthenticated, sessionActive, handleSessionExpired]);

  const handleExtendSession = useCallback(() => {
    setSessionWarning(false);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    resetSessionTimer();
  }, [resetSessionTimer]);

  const handleLogoutNow = useCallback(() => {
    handleSessionExpired();
  }, [handleSessionExpired]);

  // ============================================
  // LISTENERS DE ATIVIDADE
  // ============================================
  useEffect(() => {
    if (!isAuthenticated || !sessionActive) return;

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetSessionTimer();
    };

    // Adicionar listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, true);
    });

    // Interceptor para API calls (também conta como atividade)
    const interceptor = api.interceptors.request.use(config => {
      resetSessionTimer();
      return config;
    });

    return () => {
      // Remover listeners
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity, true);
      });
      
      // Remover interceptor
      api.interceptors.request.eject(interceptor);
    };
  }, [isAuthenticated, sessionActive, resetSessionTimer]);

  // ============================================
  // SETUP INICIAL E CLEANUP
  // ============================================
  useEffect(() => {
    if (isAuthenticated && sessionActive) {
      // Iniciar timers de sessão
      resetSessionTimer();
      
      // Timer para verificação periódica de token
      tokenCheckTimerRef.current = setInterval(
        checkTokenValidity, 
        DEFAULT_SESSION_CONFIG.checkIntervalMinutes * 60 * 1000
      );
    }

    return () => {
      // Cleanup de todos os timers
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (tokenCheckTimerRef.current) clearInterval(tokenCheckTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    };
  }, [isAuthenticated, sessionActive, resetSessionTimer, checkTokenValidity]);

  // ============================================
  // HANDLERS DE NAVEGAÇÃO
  // ============================================
  const handleBack = () => {
    navigate(-1);
  };

  const handleLogout = async () => {
    setSessionActive(false);
    await logout();
    navigate('/login');
  };

  const handleNotifications = () => {
    navigate('/notificacoes');
  };

  // ============================================
  // HELPERS
  // ============================================
  const getDashboardTitle = () => {
    if (customTitle) return customTitle;
    return DASHBOARD_TITLES[user?.tipo] || '📊 Painel do Usuário';
  };

  const getUserColor = () => {
    return USER_TYPE_COLORS[user?.tipo] || '#1976d2';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getUserInitials = () => {
    if (!user?.nome) return 'U';
    return user.nome
      .split(' ')
      .map(word => word[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  };

  // ============================================
  // RENDERIZAÇÃO
  // ============================================
  if (!user) return null;

  const userColor = getUserColor();
  const dashboardTitle = getDashboardTitle();
  const userInitials = getUserInitials();

  return (
    <>
      {/* Header Principal */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 3,
          mb: 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 2,
          border: `3px solid ${userColor}20`
        }}
      >
       {/* Lado Esquerdo - Título e Navegação */}
<Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
{/* Selector de Perfil (Automático ou papel) */}
<RoleSelector />
  {showBackButton && (
    <Tooltip title="Voltar">
      <IconButton 
        onClick={handleBack}
        sx={{ 
          color: userColor,
          '&:hover': { bgcolor: `${userColor}10` }
        }}
      >
        <ArrowBackIcon />
      </IconButton>
    </Tooltip>
  )}

  <Box>
    <Typography 
      variant="h4" 
      sx={{ 
        fontWeight: 600,
        color: userColor,
        mb: 0.5
      }}
    >
      {dashboardTitle}
    </Typography>
    <Typography variant="subtitle1" color="text.secondary">
      Bem-vindo, {user.nome}
    </Typography>
  </Box>
</Box>


        {/* Lado Direito - Ações */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Botão de Refresh */}
          <Tooltip title="Atualizar dados">
            <IconButton 
              onClick={onRefresh} 
              disabled={refreshing}
              sx={{ 
                color: userColor,
                '&:hover': { bgcolor: `${userColor}10` }
              }}
            >
              <RefreshIcon 
                sx={{ 
                  animation: refreshing ? 'spin 1s linear infinite' : 'none',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' }
                  }
                }} 
              />
            </IconButton>
          </Tooltip>

          {/* Notificações */}
          <Tooltip title="Notificações">
            <IconButton 
              onClick={handleNotifications}
              sx={{ 
                color: userColor,
                '&:hover': { bgcolor: `${userColor}10` }
              }}
            >
              <Badge badgeContent={naoLidas} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Avatar e Tipo de Usuário */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar 
              sx={{ 
                bgcolor: userColor,
                width: 40,
                height: 40,
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {userInitials}
            </Avatar>
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {user.nome}
              </Typography>
              <Chip 
                label={user.tipo} 
                size="small" 
                sx={{ 
                  bgcolor: `${userColor}20`,
                  color: userColor,
                  fontWeight: 'bold',
                  textTransform: 'capitalize'
                }}
              />
            </Box>
          </Box>

          {/* Botão de Logout */}
          <Tooltip title="Sair">
            <Button
              variant="outlined"
              color="error"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{ ml: 1 }}
            >
              Sair
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Indicador de Loading */}
      {refreshing && (
        <LinearProgress 
          sx={{ 
            mb: 2,
            '& .MuiLinearProgress-bar': {
              bgcolor: userColor
            }
          }} 
        />
      )}

      {/* Modal de Warning de Sessão */}
      <Dialog
        open={sessionWarning}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          bgcolor: 'warning.light',
          color: 'warning.contrastText'
        }}>
          <WarningIcon />
          Sessão Expirando
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Atenção!</AlertTitle>
            Sua sessão expirará em breve por inatividade.
          </Alert>
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            p: 3,
            bgcolor: 'grey.50',
            borderRadius: 2
          }}>
            <AccessTimeIcon color="warning" sx={{ fontSize: 40 }} />
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 'bold' }}>
                {formatTime(timeRemaining)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                restantes
              </Typography>
            </Box>
          </Box>

          <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
            Para manter sua sessão ativa, clique em <strong>&quot;Continuar&quot;</strong> ou 
            interaja com a página. Caso contrário, você será desconectado automaticamente.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleLogoutNow}
            color="error"
            variant="outlined"
          >
            Sair Agora
          </Button>
          <Button 
            onClick={handleExtendSession}
            color="primary"
            variant="contained"
            autoFocus
          >
            Continuar Sessão
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

DashboardHeader.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  refreshing: PropTypes.bool,
  sessionTimeout: PropTypes.number,
  showBackButton: PropTypes.bool,
  customTitle: PropTypes.string
};

export default DashboardHeader;